import taskPersonaIds from './taskPersonaIds';

/**
 * De knop 'Mijn afspraken' in de agenda: één klik op het medewerkerfilter met
 * jezelf erin, en die keuze onthouden.
 *
 * Verzoek Jan van Dijk (Duurzame Vecht, 2026-10-08): "ik wil alleen mijn eigen
 * afspraken kunnen zien". Het medewerkerfilter kon dat al, maar begon bij elk
 * bezoek aan de agenda weer op 'Alle medewerkers', en je moest jezelf in een
 * lijst opzoeken. Deze knop zet DAT filter — geen tweede filter ernaast, zodat
 * de chip 'Medewerker: …' en 'Reset' gewoon blijven kloppen.
 *
 * Nodig omdat het bestaande `taskPersonaIds` alleen per afspraak antwoordt; de
 * vraag 'wie ben ik als medewerker' en 'heb ik iets in beeld' stond nergens.
 *
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 */

/**
 * De medewerker(s) die bij de ingelogde gebruiker horen. Een afspraak draagt
 * persona-ID's, niet de gebruiker; de koppeling is `Persona.UserID`.
 *
 * @param {Array<{ID?: string, UserID?: string}>} personas
 * @param {string|undefined} userId - `auth.ID`
 * @returns {Array<string>}
 */
const ownPersonaIds = (personas, userId) => {
    if (!userId) return [];
    return (personas || [])
        .filter((persona) => persona?.UserID === userId && persona?.ID)
        .map((persona) => persona.ID);
};

/**
 * Of het medewerkerfilter op precies jezelf staat. Leeg betekent 'iedereen'.
 *
 * @param {Array<string>} selectedIds
 * @param {Array<string>} ownIds
 * @returns {boolean}
 */
const isOnlyOwn = (selectedIds, ownIds) => {
    if (!ownIds.length || selectedIds.length !== ownIds.length) return false;
    return ownIds.every((id) => selectedIds.includes(id));
};

/**
 * Of de knop er staat: alleen met de module Huishoudens (FIXtool), alleen voor
 * wie zelf medewerker is, en alleen als er in beeld een afspraak van hem staat.
 * Staat hij aan, dan blijft hij zichtbaar — anders filter je een week zonder
 * eigen afspraken leeg en is er niets meer om het mee uit te zetten.
 *
 * @param {object} args
 * @param {boolean} args.moduleOn - `householdPlanningEnabled(settings)`
 * @param {Array<string>} args.ownIds - Uit `ownPersonaIds`
 * @param {Array<object>} args.docs - De ongefilterde afspraken in beeld
 * @param {Array<string>} args.selectedIds - Het huidige medewerkerfilter
 * @returns {boolean}
 */
const showMyAppointmentsToggle = ({ moduleOn, ownIds, docs, selectedIds }) => {
    if (!moduleOn || ownIds.length === 0) return false;
    if (isOnlyOwn(selectedIds, ownIds)) return true;
    return (docs || []).some((task) => taskPersonaIds(task).some((id) => ownIds.includes(id)));
};

/**
 * Per organisatie én per gebruiker: twee mensen op één computer, of één
 * persoon in twee organisaties, hebben ieder hun eigen keuze.
 *
 * @param {string} client
 * @param {string} userId
 * @returns {string}
 */
const myAppointmentsKey = (client, userId) => `agenda-mijn-afspraken:${client}:${userId}`;

/**
 * Leest de bewaarde keuze. Ontbrekende of weigerende opslag (privévenster) telt
 * als 'uit': dit is een gemak, geen gegeven dat verloren mag gaan.
 *
 * @param {Storage|undefined} storage
 * @param {string} key
 * @returns {boolean}
 */
const readMyAppointments = (storage, key) => {
    try {
        return storage?.getItem(key) === 'true';
    } catch {
        return false;
    }
};

/**
 * @param {Storage|undefined} storage
 * @param {string} key
 * @param {boolean} on
 */
const saveMyAppointments = (storage, key, on) => {
    try {
        storage?.setItem(key, on ? 'true' : 'false');
    } catch {
        // Geen opslag beschikbaar: de keuze geldt dan alleen voor nu.
    }
};

export {
    ownPersonaIds,
    isOnlyOwn,
    showMyAppointmentsToggle,
    myAppointmentsKey,
    readMyAppointments,
    saveMyAppointments,
};
export default showMyAppointmentsToggle;
